import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBuilding,
  faTags,
  faHandshake,
  faGaugeHigh,
  faCalendarDays,
  faUserPlus,
  faPercent,
  faArrowsRotate,
  faFileContract,
  faDownload,
  faPenNib,
  faFile,
  faIdCard,
  faCreditCard,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faClockRotateLeft,
  faClock,
  faPen,
  faArrowRight,
} from '@fortawesome/pro-solid-svg-icons';
import { Auth } from '../../../core/auth/auth';
import { PartnerService } from '../../../core/services/partner.service';
import { PopupInfoService } from '../../../shared/services/popup-info.service';
import { SignaturePadModal } from '../../../shared/components/signature-pad-modal/signature-pad-modal';
import { Partner, PartnerContract, DistributionModality } from '../../../core/models/partner.model';
import { PartnerFile } from '../../../core/models/partner-file.model';
import { User } from '../../../core/models/user.model';
import {
  PartnerConditionLog,
  PARTNER_CONDITION_FIELD_LABELS,
} from '../../../core/models/partner-condition-log.model';

@Component({
  selector: 'app-mi-ficha-page',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, SignaturePadModal],
  templateUrl: './mi-ficha-page.html',
  styleUrls: ['./mi-ficha-page.scss'],
})
export class MiFichaPage implements OnInit {
  private readonly auth = inject(Auth);
  private readonly partnerService = inject(PartnerService);
  private readonly popupInfoService = inject(PopupInfoService);

  readonly faBuilding = faBuilding;
  readonly faTags = faTags;
  readonly faHandshake = faHandshake;
  readonly faGaugeHigh = faGaugeHigh;
  readonly faCalendarDays = faCalendarDays;
  readonly faUserPlus = faUserPlus;
  readonly faPercent = faPercent;
  readonly faArrowsRotate = faArrowsRotate;
  readonly faFileContract = faFileContract;
  readonly faDownload = faDownload;
  readonly faPenNib = faPenNib;
  readonly faFile = faFile;
  readonly faIdCard = faIdCard;
  readonly faCreditCard = faCreditCard;
  readonly faEnvelope = faEnvelope;
  readonly faPhone = faPhone;
  readonly faMapMarkerAlt = faMapMarkerAlt;
  readonly faClockRotateLeft = faClockRotateLeft;
  readonly faClock = faClock;
  readonly faPen = faPen;
  readonly faArrowRight = faArrowRight;

  readonly isLoading = signal(true);
  readonly isFilesLoading = signal(false);
  readonly showSignModal = signal(false);

  partner: Partner | null = null;
  sdiContactName = '';
  files: PartnerFile[] = [];

  conditionLogs: PartnerConditionLog[] = [];
  isConditionLogsLoading = false;
  conditionLogsError = false;
  conditionLogsPage = 1;
  conditionLogsLastPage = 1;
  readonly conditionLogsPerPage = 5;

  readonly relationshipTypeDescriptions: Record<string, string> = {
    Referenciador:
      'Únicamente identifica oportunidades y las deriva a SDi. Puede optar a una comisión del 10% del proyecto, pero nunca participa en el negocio recurrente.',
    Distribuidor:
      'Comercializa productos o servicios de SDi, pudiendo o no participar en la implantación. Puede participar en el negocio recurrente.',
    'Partner Estratégico':
      'Alianzas estratégicas que aportan valor al negocio, aunque no necesariamente exista una venta directa.',
    Colaborador:
      'Empresas o profesionales con los que SDi mantiene una relación de colaboración no remunerada, pudiendo compartir conocimiento, oportunidades, acciones, eventos o iniciativas cuando exista interés mutuo.',
  };

  readonly distributionModalityDescriptions: Record<string, string> = {
    Reventa:
      'Compras a SDi y facturas al cliente final. SDi realiza la implantación y el soporte.',
    'Distribución Comercial':
      'Únicamente comercializas. SDi factura al cliente final, y realiza la implantación y el soporte.',
    'Modelo Mixto':
      'La contratación, implantación y soporte se acuerdan según la operación o el producto concreto.',
    'Distribución Integral':
      'Compras a SDi, facturas al cliente final y te encargas también de la implantación y el soporte.',
  };

  readonly distributionModalityConditions: Record<
    string,
    { contracting: string; implementation: string; support: string }
  > = {
    Reventa: { contracting: 'Distribuidor', implementation: 'SDi', support: 'SDi' },
    'Distribución Comercial': { contracting: 'SDi', implementation: 'SDi', support: 'SDi' },
    'Modelo Mixto': {
      contracting: 'Según la operación',
      implementation: 'Según el producto',
      support: 'Según el producto',
    },
    'Distribución Integral': {
      contracting: 'Distribuidor',
      implementation: 'Distribuidor',
      support: 'Distribuidor',
    },
  };

  ngOnInit(): void {
    if (this.auth.infoProfile) {
      this.setFromUser(this.auth.infoProfile);
      return;
    }

    this.isLoading.set(true);
    this.auth.getProfile().subscribe({
      next: (user) => this.setFromUser(user),
      error: () => this.isLoading.set(false),
    });
  }

  private setFromUser(user: User): void {
    this.sdiContactName = user.sdi_contact?.name || 'No asignado';
    this.partner = user.partner || null;
    this.isLoading.set(false);

    if (this.partner) {
      this.loadFiles(this.partner.id.toString());
      this.loadConditionLogs(this.partner.id.toString());
    }
  }

  loadConditionLogs(partnerId: string, page = 1): void {
    this.isConditionLogsLoading = true;
    this.conditionLogsError = false;
    this.partnerService
      .getConditionLogs(partnerId, { page, per_page: this.conditionLogsPerPage })
      .subscribe({
        next: (response) => {
          this.conditionLogs =
            page === 1 ? response.data : [...this.conditionLogs, ...response.data];
          this.conditionLogsPage = response.meta.current_page;
          this.conditionLogsLastPage = response.meta.last_page;
          this.isConditionLogsLoading = false;
        },
        error: () => {
          this.conditionLogsError = true;
          this.isConditionLogsLoading = false;
        },
      });
  }

  loadMoreConditionLogs(): void {
    if (!this.partner || this.conditionLogsPage >= this.conditionLogsLastPage) return;
    this.loadConditionLogs(this.partner.id.toString(), this.conditionLogsPage + 1);
  }

  conditionLogFieldLabel(field: string): string {
    return PARTNER_CONDITION_FIELD_LABELS[field] ?? field;
  }

  loadFiles(partnerId: string): void {
    this.isFilesLoading.set(true);
    this.partnerService.getFiles(partnerId).subscribe({
      next: (files) => {
        this.files = files;
        this.isFilesLoading.set(false);
      },
      error: () => {
        this.popupInfoService.showError('Error al cargar tus documentos');
        this.isFilesLoading.set(false);
      },
    });
  }

  downloadFile(file: PartnerFile): void {
    this.partnerService.downloadFile(file).subscribe({
      next: (blob) => this.triggerDownload(blob, file.name),
      error: () => this.popupInfoService.showError('Error al descargar el archivo'),
    });
  }

  downloadContract(number: number, documentName: string | null): void {
    this.partnerService.downloadContract(number).subscribe({
      next: (blob: Blob) => this.triggerDownload(blob, documentName ?? `contrato-${number}.pdf`),
      error: () => this.popupInfoService.showError('Error al descargar el contrato'),
    });
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  openSignModal(): void {
    this.showSignModal.set(true);
  }

  closeSignModal(): void {
    this.showSignModal.set(false);
  }

  onContractSigned(): void {
    this.showSignModal.set(false);
    this.auth.getProfile().subscribe({
      next: (user) => this.setFromUser(user),
    });
  }

  get contract(): PartnerContract | null {
    return this.partner?.contract || null;
  }

  get distributionModality(): DistributionModality | null {
    return this.partner?.distribution_modality || null;
  }

  formatBytes(bytes: number = 0): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  }

  formatDate(date: string | undefined | null): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
}
